<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { formatCurrency } from '../helpers/orderHelpers';
import {
  fetchMarketplaceJson as fetchMarketplace,
  marketplaceApiUrl,
} from 'dashboard/helpers/marketplaceApi';
import Cookies from 'js-cookie';

const route = useRoute();
const accountId = route.params.accountId;

/* ------------------------------------------------------------------ tarih */
// YEREL tarihi UTC'ye çevirmeden formatla — toISOString() TR (UTC+3) saatini
// bir gün geriye kaydırıyordu ("bugün" dünü çekiyordu, 2026-07-08 bug'ı).
const fmt = d =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;
const startOfDay = d => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

const PRESETS = [
  { key: 'today', label: 'Bugün' },
  { key: 'yesterday', label: 'Dün' },
  { key: 'this_week', label: 'Bu Hafta' },
  { key: 'last_week', label: 'Geçen Hafta' },
  { key: 'this_month', label: 'Bu Ay' },
  { key: 'last_month', label: 'Geçen Ay' },
  { key: 'last_7', label: 'Son 7 Gün' },
  { key: 'last_30', label: 'Son 30 Gün' },
];

const presetRange = key => {
  const t = new Date();
  let start = startOfDay(t);
  let end = startOfDay(t);
  if (key === 'yesterday') {
    start = addDays(start, -1);
    end = addDays(end, -1);
  } else if (key === 'this_week') {
    start = addDays(start, -((t.getDay() + 6) % 7));
  } else if (key === 'last_week') {
    const mon = addDays(startOfDay(t), -((t.getDay() + 6) % 7));
    start = addDays(mon, -7);
    end = addDays(mon, -1);
  } else if (key === 'this_month') {
    start = new Date(t.getFullYear(), t.getMonth(), 1);
  } else if (key === 'last_month') {
    start = new Date(t.getFullYear(), t.getMonth() - 1, 1);
    end = new Date(t.getFullYear(), t.getMonth(), 0);
  } else if (key === 'last_7') {
    start = addDays(start, -6);
  } else if (key === 'last_30') {
    start = addDays(start, -29);
  }
  return { start: fmt(start), end: fmt(end) };
};

/* ------------------------------------------------------------------ durum */
const preset = ref('today');
const startDate = ref(presetRange('today').start);
const endDate = ref(presetRange('today').end);
const draftStart = ref(startDate.value);
const draftEnd = ref(endDate.value);
const showCustom = ref(false);

const loading = ref(true);
const refreshing = ref(false);
const errorMsg = ref('');
const data = ref(null);

const search = ref('');
const sortKey = ref('revenue_net');
const showBot = ref(true);
const helpKey = ref(null);
const openAgent = ref(null);
const chartMetric = ref('revenue');

const SORTS = [
  { key: 'revenue_net', label: 'Ciroya göre' },
  { key: 'orders_net', label: 'Siparişe göre' },
  { key: 'conversations', label: 'Konuşmaya göre' },
  { key: 'conversion', label: 'Dönüşüme göre' },
  { key: 'worked_hours', label: 'Mesaiye göre' },
  // Bu ikisinde KÜÇÜK olan iyidir → artan sıralanır, ölçümü olmayan sona düşer.
  {
    key: 'first_response_min',
    label: 'İlk yanıta göre (hızlı önce)',
    asc: true,
  },
  { key: 'return_rate', label: 'İade oranına göre (düşük önce)', asc: true },
];

const HELP = {
  konusma: {
    title: '💬 Cevaplanan Konuşma',
    text: 'Seçili dönemde bu temsilcinin GERÇEKTEN konuştuğu farklı müşteri sayısı. Bir müşteriyle 10 kez yazışsa da 1 konuşma sayılır. Bir konuşmaya iki temsilci de yazdıysa ikisinin de hanesine yazılır. TOPLU MESAJLAR SAYILMAZ: aynı metin onlarca sohbete gönderildiyse (duyuru, hatırlatma) bunlar konuşma sayılmaz, satırın altında "+N toplu" olarak ayrıca görünür. Toplu mesaj gönderilen bir müşteri sonrasında cevap yazdıysa ya da temsilci ona kişiye özel bir şey yazdıysa o sohbet normal konuşma olarak sayılır. Böylece duyuru göndermek konuşma sayısını da dönüşüm oranını da bozmaz.',
  },
  siparis: {
    title: '📦 Net Sipariş',
    text: 'Temsilcinin açtığı siparişlerden İPTAL VE İADE OLANLAR ÇIKARILMIŞ hâli. Eski karnede iptaller de sayılıyordu ve rakamlar olduğundan yüksek görünüyordu. İptal sayısı ayrıca gösterilir.',
  },
  ciro: {
    title: '💰 Net Ciro',
    text: 'Temsilcinin getirdiği, iptal ve iadeler düşülmüş toplam tutar. Emeğin paraya dönen gerçek kısmı budur. İptal edilen tutar ayrı satırda görünür.',
  },
  donusum: {
    title: '📈 Dönüşüm Oranı',
    text: 'Her 100 konuşmadan kaçının siparişe döndüğü. Satışa çevirme gücünü gösterir. Düşükse ürün, fiyat ya da stok sorunu da olabilir — tek başına temsilciyi suçlamak doğru olmaz.',
  },
  mesai: {
    title: '🕐 Aktif Mesai',
    text: 'Moladan arınmış GERÇEK çalışma süresi: mesajlar arasındaki 30 dakikadan uzun boşluklar (yemek, ara) düşülerek hesaplanır. Örneğin 12:00–17:00 arası görünen ama 2 saat molası olan biri "3 saat aktif" sayılır. Çok günlü aralıklarda toplam süre ve kaç gün çalıştığı gösterilir.',
  },
  ilkyanit: {
    title: '⚡ İlk Yanıt Süresi (mesai içi)',
    text: "KİMİN HANESİNE YAZILIR: Bir konuşmada ilk cevabı KİM yazdıysa süre onun hanesine geçer. Konuşmanın kime atandığının hiçbir etkisi yoktur — atanmamış (havuzdaki) bir müşteriye cevap yazarsan o süre senin hanene yazılır; üstüne almana da gerek yoktur, cevabı yazman yeterlidir. Uygulamada ekibin ilk yanıtlarının neredeyse tamamı zaten atanmamış konuşmalarda gerçekleşiyor. Yani ölçülen şey 'kendi üzerimdekilere hızlı dönmek' değil, HAVUZDA BEKLEYEN MÜŞTERİYE İLK DÖNÜŞ HIZIDIR. NE ÖLÇÜLÜR: müşteri yazdıktan sonra ilk cevabın gelmesi kaç dakika sürdü — konuşma başına SADECE BİR kez. İkinci, üçüncü cevaplar ve zaten cevaplanmış bir konuşmaya sonradan yazmak sayılmaz. Bot cevabı ilk yanıt sayılmaz; bot konuşmayı ekibe devrettiyse süre devir anından başlar. Gösterilen değer ORTANCA (medyan), tek bir uç örnek bozmasın diye. YALNIZCA 09:00–21:00 arasında gelen mesajlar sayılır: gece gelen mesaja sabah 09:00'da cevap verildiğinde süre 9-10 saat çıkıyor ve bu, sabah vardiyasını haksız yere en yavaş gösteriyordu. Ölçülen konuşma sayısı yanında yazar; sayı küçükse (10 altı) tek bir gecikme oranı çok oynatır.",
  },
  gece: {
    title: '🌙 Gece Biriken',
    text: 'Kapanış saatinden sonra (21:00–09:00) gelen ve ertesi gün açılan konuşmalar. Buradaki uzun süre temsilcinin yavaşlığı DEĞİL, o saatte kimsenin olmamasıdır — sabah vardiyası günü bu kuyruğu açarak başlar. Bu satır bir performans notu değil, kapsama açığının ölçüsüdür: sayı büyükse akşam/gece vardiyası tartışılmalı.',
  },
  iade: {
    title: '↩️ İade',
    text: 'Soldaki sayı: seçili dönemde açılan iade talebi (siparişi bu temsilcinin açtığı). Yanındaki oran ise dönemin değil, SON 90 GÜNÜN oranıdır — çünkü iade siparişten haftalar sonra gelir, haftalık oran hem 0-1 gürültüsüne düşer hem de yeni siparişlerin iadesi henüz gelmediği için olduğundan iyi görünür. Oranın altındaki (iade/sipariş) kesri kaç örnekten hesaplandığını gösterir. Müşterinin siteden kendi verdiği siparişlerin iadesi kimsenin hanesine yazılmaz.',
  },
  atanmamis: {
    title: '🧾 Temsilcisiz Sipariş',
    text: 'Sitede müşterinin kendi başına verdiği, hiçbir temsilcinin sohbetinden geçmeyen siparişler. Bunlar kimsenin hanesine yazılmaz — ekip karnesi yalnızca sohbet üzerinden açılan siparişleri ölçer. Sayının yüksek olması normaldir.',
  },
};

/* ------------------------------------------------------------------ veri */
const authHeaders = () => {
  const raw = Cookies.get('cw_d_session_info');
  if (!raw) return {};
  try {
    const p = JSON.parse(raw);
    return {
      'access-token': p['access-token'],
      'token-type': p['token-type'],
      client: p.client,
      expiry: p.expiry,
      uid: p.uid,
    };
  } catch {
    return {};
  }
};

const load = async () => {
  loading.value = true;
  errorMsg.value = '';
  try {
    data.value = await fetchMarketplace(
      accountId,
      `reports/team_scorecard?start=${startDate.value}&end=${endDate.value}`
    );
  } catch (e) {
    data.value = null;
    errorMsg.value =
      'Karne verisi yüklenemedi. Bağlantıyı kontrol edip tekrar deneyin.';
  }
  loading.value = false;
};

const refreshAgents = async () => {
  refreshing.value = true;
  try {
    await fetch(marketplaceApiUrl(accountId, 'reports/refresh_order_agents'), {
      method: 'POST',
      headers: authHeaders(),
    });
    await new Promise(r => {
      setTimeout(r, 2500);
    });
    await load();
  } catch {
    errorMsg.value = 'Tazeleme başlatılamadı.';
  }
  refreshing.value = false;
};

const applyPreset = key => {
  preset.value = key;
  showCustom.value = false;
  const r = presetRange(key);
  startDate.value = r.start;
  endDate.value = r.end;
  draftStart.value = r.start;
  draftEnd.value = r.end;
  load();
};

const applyCustom = () => {
  if (!draftStart.value || !draftEnd.value) return;
  let s = draftStart.value;
  let e = draftEnd.value;
  if (s > e) [s, e] = [e, s]; // ters girilirse sessizce düzelt
  startDate.value = s;
  endDate.value = e;
  draftStart.value = s;
  draftEnd.value = e;
  preset.value = 'custom';
  load();
};

// Dönemi olduğu gibi geri/ileri kaydır (aynı gün sayısıyla).
const stepPeriod = dir => {
  const s = new Date(`${startDate.value}T00:00:00`);
  const e = new Date(`${endDate.value}T00:00:00`);
  const len = Math.round((e - s) / 86400000) + 1;
  const ns = addDays(s, dir * len);
  const ne = addDays(e, dir * len);
  if (dir > 0 && ns > startOfDay(new Date())) return; // geleceğe gitme
  startDate.value = fmt(ns);
  endDate.value = fmt(ne);
  draftStart.value = startDate.value;
  draftEnd.value = endDate.value;
  preset.value = 'custom';
  load();
};

const canStepForward = computed(() => {
  const e = new Date(`${endDate.value}T00:00:00`);
  return e < startOfDay(new Date());
});

/* --------------------------------------------------------------- türetme */
const team = computed(() => data.value?.team || {});
const meta = computed(() => data.value?.meta || {});
const allAgents = computed(() => data.value?.agents || []);

const norm = s => (s || '').toLocaleLowerCase('tr-TR').trim();

const rows = computed(() => {
  let list = allAgents.value.filter(a => showBot.value || !a.is_bot);
  const q = norm(search.value);
  if (q) list = list.filter(a => norm(a.name).includes(q));
  const k = sortKey.value;
  const asc = SORTS.find(x => x.key === k)?.asc;
  return [...list].sort((a, b) => {
    // Artan metriklerde ölçümü olmayan (null) satır en sona gitmeli, başa değil.
    const av = a[k] ?? (asc ? Infinity : -1);
    const bv = b[k] ?? (asc ? Infinity : -1);
    if (bv !== av) return asc ? av - bv : bv - av;
    return b.revenue_net - a.revenue_net;
  });
});

const humans = computed(() => rows.value.filter(a => !a.is_bot));
const botRows = computed(() => rows.value.filter(a => a.is_bot));

const periodLabel = computed(() => {
  const opt = { day: 'numeric', month: 'long' };
  const s = new Date(`${startDate.value}T00:00:00`);
  const e = new Date(`${endDate.value}T00:00:00`);
  if (startDate.value === endDate.value)
    return s.toLocaleDateString('tr-TR', { ...opt, weekday: 'long' });
  return `${s.toLocaleDateString('tr-TR', opt)} — ${e.toLocaleDateString(
    'tr-TR',
    { ...opt, year: 'numeric' }
  )}`;
});

const prevLabel = computed(() => {
  const p = data.value?.previous_range;
  if (!p) return '';
  const opt = { day: 'numeric', month: 'short' };
  const s = new Date(`${p.start}T00:00:00`).toLocaleDateString('tr-TR', opt);
  const e = new Date(`${p.end}T00:00:00`).toLocaleDateString('tr-TR', opt);
  return p.start === p.end ? s : `${s} – ${e}`;
});

// Yüzde değişim; önceki dönem sıfırsa oran anlamsız olur → null döner.
const delta = (now, before) => {
  if (before === null || before === undefined) return null;
  if (!before) return now ? null : 0;
  return ((now - before) / before) * 100;
};

const teamDeltas = computed(() => {
  const p = team.value.previous || {};
  return {
    conversations: delta(team.value.conversations, p.conversations),
    orders_net: delta(team.value.orders_net, p.orders_net),
    revenue_net: delta(team.value.revenue_net, p.revenue_net),
    conversion: delta(team.value.conversion, p.conversion),
    first_response_min: delta(
      team.value.first_response_min,
      p.first_response_min
    ),
  };
});

const fmtDelta = v =>
  v === null || v === undefined
    ? ''
    : `${v >= 0 ? '↑' : '↓'} %${Math.abs(v).toFixed(0)}`;
const deltaClass = v => {
  if (v === null || v === undefined) return 'text-n-slate-10';
  if (v > 0.5) return 'text-n-teal-11';
  if (v < -0.5) return 'text-n-ruby-11';
  return 'text-n-slate-10';
};

/* ---------------------------------------------------------------- grafik */
const chart = computed(() => {
  const d = data.value?.daily || [];
  const key = chartMetric.value;
  const max = Math.max(...d.map(x => x[key] || 0), 1);
  return d.map(x => ({
    ...x,
    pct: Math.round(((x[key] || 0) / max) * 100),
    label: new Date(`${x.date}T00:00:00`).toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'short',
    }),
  }));
});
const chartValue = row =>
  chartMetric.value === 'revenue'
    ? formatCurrency(row.revenue)
    : `${row[chartMetric.value]}`;

/* ----------------------------------------------------------------- dışa */
const exportCsv = () => {
  const head = [
    'Temsilci',
    'Konuşma',
    'Toplu Mesaj (sayılmayan)',
    'Mesaj',
    'Net Sipariş',
    'İptal',
    'Net Ciro',
    'İptal Tutarı',
    'Ort. Sepet',
    'Dönüşüm %',
    'İlk Yanıt Mesai İçi (dk)',
    'İlk Yanıt Ölçülen Konuşma',
    'Gece Biriken (dk)',
    'Gece Biriken Konuşma',
    'İade (dönem)',
    'İade Oranı 90g %',
    'İade/Sipariş 90g',
    'Aktif Mesai (saat)',
    'Çalışılan Gün',
  ];
  const lines = rows.value.map(a =>
    [
      a.name,
      a.conversations,
      a.broadcast_conversations,
      a.messages,
      a.orders_net,
      a.orders_cancelled,
      a.revenue_net,
      a.revenue_cancelled,
      a.avg_order_value,
      a.conversion ?? '',
      a.first_response_min ?? '',
      a.first_response_count ?? '',
      a.offhours_response_min ?? '',
      a.offhours_response_count ?? '',
      a.returns_count ?? '',
      a.return_rate ?? '',
      `${a.return_rate_returns ?? 0}/${a.return_rate_orders ?? 0}`,
      a.worked_hours ?? '',
      a.active_days ?? '',
    ].join(';')
  );
  // \uFEFF (BOM) = Excel'in Türkçe karakterleri doğru açması için gerekli
  const csv = `\uFEFF${head.join(';')}\n${lines.join('\n')}`;
  const url = URL.createObjectURL(
    new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  );
  const a = document.createElement('a');
  a.href = url;
  a.download = `ekip-karnesi-${startDate.value}_${endDate.value}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const initials = a => a.initials || '?';
const nf = n => (n ?? 0).toLocaleString('tr-TR');

// Dakika → okunur süre. Ölçüm yoksa 0 değil '—' gösterilir: "0 dk" sahte bir
// başarı gibi okunur, oysa o dönemde hiç ilk yanıt ölçülmemiş demektir.
const fmtDuration = min => {
  if (min === null || min === undefined) return '—';
  if (min < 1) return '<1 dk';
  if (min < 60) return `${Math.round(min)} dk`;
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return m ? `${h} sa ${m} dk` : `${h} sa`;
};

const frtClass = min => {
  if (min === null || min === undefined) return 'text-n-slate-10';
  if (min <= 10) return 'text-n-teal-11';
  if (min <= 30) return 'text-n-slate-12';
  return 'text-n-amber-11';
};

const returnClass = rate => {
  if (rate === null || rate === undefined) return 'text-n-slate-10';
  if (rate >= 5) return 'text-n-ruby-11';
  if (rate >= 3) return 'text-n-amber-11';
  return 'text-n-teal-11';
};

watch(showCustom, v => {
  if (v) {
    draftStart.value = startDate.value;
    draftEnd.value = endDate.value;
  }
});

onMounted(load);
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-auto bg-n-background">
    <div class="max-w-6xl w-full mx-auto px-4 sm:px-6 py-6 sm:py-8">
      <!-- ============================ BAŞLIK ============================ -->
      <div class="flex flex-wrap items-start justify-between gap-4 mb-5">
        <div class="min-w-0">
          <div
            class="text-xs font-bold uppercase tracking-widest text-n-slate-10"
          >
            Müşteri Hizmetleri
          </div>
          <h1
            class="text-3xl sm:text-4xl font-bold text-n-slate-12 mt-1 leading-tight"
          >
            🏆 Ekip Karnesi
          </h1>
          <div class="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2">
            <span class="text-base font-bold text-woot-600 dark:text-woot-400">
              📅 {{ periodLabel }}
            </span>
            <span v-if="prevLabel" class="text-sm text-n-slate-10">
              kıyas: {{ prevLabel }}
            </span>
          </div>
        </div>

        <div class="flex items-center gap-2">
          <button
            class="rounded-lg border border-n-weak px-3 py-2 text-sm font-semibold text-n-slate-11 hover:bg-n-solid-2 focus:outline-none focus:ring-2 focus:ring-woot-500 disabled:opacity-50"
            :disabled="refreshing || loading"
            title="Bugünün siparişlerinde temsilci atamasını tazele"
            @click="refreshAgents"
          >
            {{ refreshing ? '⏳ Tazeleniyor…' : '🔄 Tazele' }}
          </button>
          <button
            class="rounded-lg border border-n-weak px-3 py-2 text-sm font-semibold text-n-slate-11 hover:bg-n-solid-2 focus:outline-none focus:ring-2 focus:ring-woot-500 disabled:opacity-50"
            :disabled="!rows.length"
            @click="exportCsv"
          >
            ⬇️ Excel
          </button>
        </div>
      </div>

      <!-- ============================ FİLTRE ============================ -->
      <div
        class="rounded-2xl border border-n-weak bg-n-solid-1 p-4 mb-5 flex flex-col gap-3"
      >
        <div class="flex flex-wrap items-center gap-2">
          <button
            class="rounded-lg border border-n-weak w-9 h-9 text-lg font-bold text-n-slate-11 hover:bg-n-solid-2 focus:outline-none focus:ring-2 focus:ring-woot-500"
            title="Önceki dönem"
            aria-label="Önceki dönem"
            @click="stepPeriod(-1)"
          >
            ‹
          </button>
          <button
            v-for="p in PRESETS"
            :key="p.key"
            class="rounded-lg px-3.5 py-2 text-sm font-semibold border transition-colors focus:outline-none focus:ring-2 focus:ring-woot-500"
            :class="
              preset === p.key
                ? 'bg-woot-600 border-woot-600 text-white'
                : 'border-n-weak text-n-slate-11 hover:bg-n-solid-2'
            "
            @click="applyPreset(p.key)"
          >
            {{ p.label }}
          </button>
          <button
            class="rounded-lg px-3.5 py-2 text-sm font-semibold border transition-colors focus:outline-none focus:ring-2 focus:ring-woot-500"
            :class="
              preset === 'custom'
                ? 'bg-woot-600 border-woot-600 text-white'
                : 'border-n-weak text-n-slate-11 hover:bg-n-solid-2'
            "
            @click="showCustom = !showCustom"
          >
            🗓 Tarih Seç
          </button>
          <button
            class="rounded-lg border border-n-weak w-9 h-9 text-lg font-bold text-n-slate-11 hover:bg-n-solid-2 focus:outline-none focus:ring-2 focus:ring-woot-500 disabled:opacity-40"
            :disabled="!canStepForward"
            title="Sonraki dönem"
            aria-label="Sonraki dönem"
            @click="stepPeriod(1)"
          >
            ›
          </button>
        </div>

        <!-- özel tarih aralığı -->
        <div
          v-if="showCustom"
          class="flex flex-wrap items-end gap-3 pt-1 border-t border-n-weak"
        >
          <label class="flex flex-col gap-1">
            <span class="text-xs font-bold uppercase text-n-slate-10"
              >Başlangıç</span
            >
            <input
              v-model="draftStart"
              type="date"
              class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-base text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500"
            />
          </label>
          <label class="flex flex-col gap-1">
            <span class="text-xs font-bold uppercase text-n-slate-10"
              >Bitiş</span
            >
            <input
              v-model="draftEnd"
              type="date"
              class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-base text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500"
            />
          </label>
          <button
            class="rounded-lg bg-woot-600 px-4 py-2 text-base font-semibold text-white hover:bg-woot-700 focus:outline-none focus:ring-2 focus:ring-woot-500"
            @click="applyCustom"
          >
            Uygula
          </button>
        </div>

        <!-- arama / sıralama / bot -->
        <div
          class="flex flex-wrap items-center gap-3 pt-1 border-t border-n-weak"
        >
          <input
            v-model="search"
            type="search"
            placeholder="Temsilci ara…"
            class="flex-1 min-w-[180px] rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-base text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-woot-500"
          />
          <label class="flex items-center gap-2">
            <span class="text-sm font-semibold text-n-slate-11">Sırala</span>
            <select
              v-model="sortKey"
              class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-base text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500"
            >
              <option v-for="s in SORTS" :key="s.key" :value="s.key">
                {{ s.label }}
              </option>
            </select>
          </label>
          <label
            class="flex items-center gap-2 text-sm font-semibold text-n-slate-11 cursor-pointer"
          >
            <input v-model="showBot" type="checkbox" class="w-4 h-4" />
            Botu göster
          </label>
        </div>
      </div>

      <!-- ============================ UYARI ============================ -->
      <div
        v-if="errorMsg"
        class="rounded-xl bg-n-ruby-3 text-n-ruby-11 px-4 py-3 mb-4 text-base font-semibold flex items-center justify-between gap-3"
      >
        <span>{{ errorMsg }}</span>
        <button
          class="rounded-lg border border-n-ruby-6 px-3 py-1.5 text-sm font-bold hover:bg-n-ruby-3"
          @click="load"
        >
          Tekrar dene
        </button>
      </div>

      <div
        v-if="!loading && meta.agents_stale"
        class="rounded-xl bg-n-amber-3 text-n-amber-11 px-4 py-3 mb-4 text-sm font-semibold"
      >
        ⚠️ Sipariş–temsilci eşleşmesi
        {{ meta.agents_synced_at || 'bilinmeyen bir zamandan' }} beri
        tazelenmedi. Son saatlerde açılan siparişler eksik görünebilir —
        “Tazele” düğmesine basabilirsiniz.
      </div>

      <!-- ========================== YÜKLENİYOR ========================== -->
      <div v-if="loading" class="flex flex-col gap-3" aria-busy="true">
        <div class="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <div
            v-for="i in 5"
            :key="i"
            class="h-28 rounded-2xl bg-n-solid-2 animate-pulse"
          />
        </div>
        <div
          v-for="i in 4"
          :key="`r${i}`"
          class="h-24 rounded-2xl bg-n-solid-2 animate-pulse"
        />
      </div>

      <template v-else-if="data">
        <!-- ========================= EKİP ÖZETİ ========================= -->
        <div class="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-6">
          <div class="rounded-2xl border border-n-weak bg-n-solid-1 p-4">
            <div class="text-sm font-semibold text-n-slate-10">
              Cevaplanan kişi
            </div>
            <div
              class="text-3xl font-bold text-n-slate-12 tabular-nums mt-1 leading-none"
            >
              {{ nf(team.conversations) }}
            </div>
            <div
              class="text-sm font-bold mt-2"
              :class="deltaClass(teamDeltas.conversations)"
            >
              {{ fmtDelta(teamDeltas.conversations) || '—' }}
            </div>
            <div
              v-if="team.broadcast_conversations"
              class="text-xs text-n-slate-10 mt-1"
              title="Aynı metnin toplu gönderildiği sohbetler konuşma sayılmaz."
            >
              +{{ nf(team.broadcast_conversations) }} toplu mesaj (sayılmadı)
            </div>
          </div>
          <div class="rounded-2xl border border-n-weak bg-n-solid-1 p-4">
            <div class="text-sm font-semibold text-n-slate-10">Net sipariş</div>
            <div
              class="text-3xl font-bold text-n-slate-12 tabular-nums mt-1 leading-none"
            >
              {{ nf(team.orders_net) }}
            </div>
            <div
              class="text-sm font-bold mt-2"
              :class="deltaClass(teamDeltas.orders_net)"
            >
              {{ fmtDelta(teamDeltas.orders_net) || '—' }}
            </div>
          </div>
          <div class="rounded-2xl border border-n-weak bg-n-solid-1 p-4">
            <div class="text-sm font-semibold text-n-slate-10">Net ciro</div>
            <div
              class="text-3xl font-bold text-n-slate-12 tabular-nums mt-1 leading-none"
            >
              {{ formatCurrency(team.revenue_net) }}
            </div>
            <div
              class="text-sm font-bold mt-2"
              :class="deltaClass(teamDeltas.revenue_net)"
            >
              {{ fmtDelta(teamDeltas.revenue_net) || '—' }}
            </div>
          </div>
          <div class="rounded-2xl border border-n-weak bg-n-solid-1 p-4">
            <div class="text-sm font-semibold text-n-slate-10">Aktif mesai</div>
            <div
              class="text-3xl font-bold text-n-slate-12 tabular-nums mt-1 leading-none"
            >
              {{ team.worked_hours }}<span class="text-lg"> saat</span>
            </div>
            <div class="text-sm text-n-slate-10 mt-2">
              {{ team.agent_count }} temsilci
            </div>
          </div>
          <div class="rounded-2xl border border-n-weak bg-n-solid-1 p-4">
            <div class="text-sm font-semibold text-n-slate-10">Dönüşüm</div>
            <div
              class="text-3xl font-bold text-n-slate-12 tabular-nums mt-1 leading-none"
            >
              %{{ team.conversion ?? 0 }}
            </div>
            <div
              class="text-sm font-bold mt-2"
              :class="deltaClass(teamDeltas.conversion)"
            >
              {{ fmtDelta(teamDeltas.conversion) || '—' }}
            </div>
          </div>
          <div class="rounded-2xl border border-n-weak bg-n-solid-1 p-4">
            <button
              class="text-sm font-semibold text-n-slate-10 text-left focus:outline-none focus:ring-2 focus:ring-woot-500 rounded"
              @click="helpKey = 'ilkyanit'"
            >
              İlk yanıt ⓘ
            </button>
            <div
              class="text-3xl font-bold tabular-nums mt-1 leading-none"
              :class="frtClass(team.first_response_min)"
            >
              {{ fmtDuration(team.first_response_min) }}
            </div>
            <div class="text-sm text-n-slate-10 mt-2">
              {{ meta.coverage_hours }} arası
            </div>
            <div class="text-sm text-n-slate-10 mt-1">
              Atanmamış havuz dahil: ilk cevabı yazana yazılır.
            </div>
          </div>
        </div>

        <!-- Ölçüm kuralı: rakamın ne olduğu ekranda yazsın, sözlü aktarıma kalmasın -->
        <div
          v-if="team.offhours_response_count"
          class="rounded-xl border border-n-amber-6 bg-n-amber-2 px-4 py-3 mb-6 text-sm text-n-amber-12"
        >
          🌙 Bu dönemde <b>{{ nf(team.offhours_response_count) }}</b> konuşma
          kapanış saatinden sonra ({{ meta.coverage_hours }} dışında) geldi ve
          ortalama <b>{{ fmtDuration(team.offhours_response_min) }}</b> bekledi.
          Bu süre ilk yanıt puanına <b>katılmaz</b> — kimsenin çevrimiçi
          olmadığı saatler temsilcinin hanesine yazılmamalı. Sabah vardiyası
          günü bu kuyruğu açarak başlar.
          <button
            class="underline decoration-dotted focus:outline-none focus:ring-2 focus:ring-woot-500 rounded"
            @click="helpKey = 'gece'"
          >
            ayrıntı
          </button>
        </div>

        <!-- iptal + atanmamış bilgi şeridi -->
        <div
          class="flex flex-wrap items-center gap-x-5 gap-y-2 mb-6 text-sm text-n-slate-11"
        >
          <span v-if="team.orders_cancelled">
            🚫 <b>{{ nf(team.orders_cancelled) }}</b> iptal/iade ({{
              formatCurrency(team.revenue_cancelled)
            }}) net rakamlardan düşüldü
          </span>
          <button
            class="underline decoration-dotted hover:text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500 rounded"
            @click="helpKey = 'atanmamis'"
          >
            🧾 {{ nf(meta.unassigned_orders) }} sipariş temsilcisiz (site
            üzerinden) — nedir?
          </button>
          <span class="text-n-slate-10"> Veri: {{ meta.generated_at }} </span>
        </div>

        <!-- ========================== GÜNLÜK SERİ ========================== -->
        <div
          v-if="chart.length > 1"
          class="rounded-2xl border border-n-weak bg-n-solid-1 p-4 sm:p-5 mb-8"
        >
          <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h2 class="text-lg font-bold text-n-slate-12">Gün gün gidişat</h2>
            <div class="flex gap-1">
              <button
                v-for="m in [
                  { k: 'revenue', l: 'Ciro' },
                  { k: 'orders', l: 'Sipariş' },
                  { k: 'conversations', l: 'Konuşma' },
                ]"
                :key="m.k"
                class="rounded-lg px-3 py-1.5 text-sm font-semibold border focus:outline-none focus:ring-2 focus:ring-woot-500"
                :class="
                  chartMetric === m.k
                    ? 'bg-woot-600 border-woot-600 text-white'
                    : 'border-n-weak text-n-slate-11 hover:bg-n-solid-2'
                "
                @click="chartMetric = m.k"
              >
                {{ m.l }}
              </button>
            </div>
          </div>
          <div class="flex items-end gap-[3px] h-36 overflow-x-auto">
            <div
              v-for="c in chart"
              :key="c.date"
              class="flex-1 min-w-[8px] flex flex-col justify-end h-full group relative"
              :title="`${c.label}: ${chartValue(c)}`"
            >
              <div
                class="w-full rounded-t bg-woot-500 group-hover:bg-woot-700 transition-colors"
                :style="{ height: `${Math.max(c.pct, 2)}%` }"
              />
            </div>
          </div>
          <div class="flex justify-between text-xs text-n-slate-10 mt-2">
            <span>{{ chart[0].label }}</span>
            <span>{{ chart[chart.length - 1].label }}</span>
          </div>
        </div>

        <!-- ========================== TEMSİLCİLER ========================== -->
        <div class="flex items-baseline gap-2 mb-3">
          <h2 class="text-xl font-bold text-n-slate-12">
            Müşteri Temsilcileri
          </h2>
          <span class="text-sm text-n-slate-10">
            {{ SORTS.find(s => s.key === sortKey).label.toLowerCase() }}
            sıralı
          </span>
        </div>

        <div
          v-if="!humans.length"
          class="rounded-2xl border border-dashed border-n-weak p-10 text-center text-n-slate-10 text-base"
        >
          <template v-if="search">
            “{{ search }}” için temsilci bulunamadı.
          </template>
          <template v-else>
            Bu dönemde temsilci etkinliği yok. Farklı bir tarih aralığı deneyin.
          </template>
        </div>

        <div class="flex flex-col gap-3 mb-10">
          <div
            v-for="(r, i) in humans"
            :key="r.user_id"
            class="rounded-2xl border border-n-weak bg-n-solid-1 overflow-hidden"
          >
            <div
              class="p-4 sm:p-5 flex flex-col lg:flex-row lg:items-center gap-4"
            >
              <!-- kimlik -->
              <div class="flex items-center gap-3 lg:w-64 shrink-0">
                <span
                  class="text-lg font-bold text-n-slate-9 w-6 text-center tabular-nums"
                  >{{ i + 1 }}</span
                >
                <span
                  class="w-12 h-12 rounded-full bg-woot-100 dark:bg-woot-900 text-woot-700 dark:text-woot-200 flex items-center justify-center font-bold text-lg shrink-0"
                  >{{ initials(r) }}</span
                >
                <div class="min-w-0">
                  <div
                    class="font-bold text-n-slate-12 text-lg truncate flex items-center gap-2"
                  >
                    {{ r.name }}
                    <span
                      v-if="r.left_team"
                      class="text-xs font-bold px-2 py-0.5 rounded bg-n-amber-3 text-n-amber-11 shrink-0"
                      >ayrıldı</span
                    >
                  </div>
                  <button
                    v-if="r.worked_hours"
                    class="text-sm text-n-slate-10 hover:text-n-slate-12 text-left focus:outline-none focus:ring-2 focus:ring-woot-500 rounded"
                    @click="helpKey = 'mesai'"
                  >
                    ⏱ {{ r.worked_hours }} saat aktif
                    <template v-if="r.active_days > 1">
                      · {{ r.active_days }} gün
                    </template>
                    <template v-else>
                      · {{ r.first_at }}–{{ r.last_at }}
                    </template>
                  </button>
                </div>
              </div>

              <!-- metrikler -->
              <div
                class="flex-1 grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3"
              >
                <div class="text-center">
                  <div
                    class="text-2xl font-bold text-n-slate-12 tabular-nums leading-none"
                  >
                    {{ nf(r.conversations) }}
                  </div>
                  <button
                    class="text-xs text-n-slate-10 hover:text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500 rounded mt-1"
                    @click="helpKey = 'konusma'"
                  >
                    💬 konuşma
                  </button>
                  <div
                    v-if="r.broadcast_conversations"
                    class="text-xs text-n-slate-10 mt-0.5"
                    title="Aynı metnin toplu gönderildiği sohbetler. Konuşma sayısına ve dönüşüme katılmaz."
                  >
                    +{{ nf(r.broadcast_conversations) }} toplu
                  </div>
                  <div
                    v-if="r.previous"
                    class="text-xs font-bold"
                    :class="
                      deltaClass(
                        delta(r.conversations, r.previous.conversations)
                      )
                    "
                  >
                    {{
                      fmtDelta(delta(r.conversations, r.previous.conversations))
                    }}
                  </div>
                </div>
                <div class="text-center">
                  <div
                    class="text-2xl font-bold text-n-slate-12 tabular-nums leading-none"
                  >
                    {{ nf(r.orders_net) }}
                  </div>
                  <button
                    class="text-xs text-n-slate-10 hover:text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500 rounded mt-1"
                    @click="helpKey = 'siparis'"
                  >
                    📦 net sipariş
                  </button>
                  <div
                    v-if="r.orders_cancelled"
                    class="text-xs font-bold text-n-ruby-11"
                  >
                    {{ r.orders_cancelled }} iptal
                  </div>
                </div>
                <div class="text-center">
                  <div
                    class="text-2xl font-bold text-n-slate-12 tabular-nums leading-none"
                  >
                    {{ formatCurrency(r.revenue_net) }}
                  </div>
                  <button
                    class="text-xs text-n-slate-10 hover:text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500 rounded mt-1"
                    @click="helpKey = 'ciro'"
                  >
                    💰 net ciro
                  </button>
                  <div
                    v-if="r.previous"
                    class="text-xs font-bold"
                    :class="
                      deltaClass(delta(r.revenue_net, r.previous.revenue_net))
                    "
                  >
                    {{ fmtDelta(delta(r.revenue_net, r.previous.revenue_net)) }}
                  </div>
                </div>
                <div class="text-center">
                  <div
                    class="text-2xl font-bold tabular-nums leading-none"
                    :class="
                      (r.conversion || 0) >= 5
                        ? 'text-n-teal-11'
                        : 'text-n-slate-12'
                    "
                  >
                    {{ r.conversion === null ? '—' : `%${r.conversion}` }}
                  </div>
                  <button
                    class="text-xs text-n-slate-10 hover:text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500 rounded mt-1"
                    @click="helpKey = 'donusum'"
                  >
                    📈 dönüşüm
                  </button>
                </div>
                <div class="text-center">
                  <div
                    class="text-2xl font-bold tabular-nums leading-none"
                    :class="frtClass(r.first_response_min)"
                  >
                    {{ fmtDuration(r.first_response_min) }}
                  </div>
                  <button
                    class="text-xs text-n-slate-10 hover:text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500 rounded mt-1"
                    @click="helpKey = 'ilkyanit'"
                  >
                    ⚡ ilk yanıt
                  </button>
                  <div
                    v-if="r.first_response_count"
                    class="text-xs text-n-slate-10"
                  >
                    {{ r.first_response_count }} konuşma
                  </div>
                </div>
                <div class="text-center">
                  <div
                    class="text-2xl font-bold text-n-slate-12 tabular-nums leading-none"
                  >
                    {{ nf(r.returns_count) }}
                  </div>
                  <button
                    class="text-xs text-n-slate-10 hover:text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500 rounded mt-1"
                    @click="helpKey = 'iade'"
                  >
                    ↩️ iade
                  </button>
                  <div
                    v-if="r.return_rate !== null && r.return_rate !== undefined"
                    class="text-xs font-bold"
                    :class="returnClass(r.return_rate)"
                  >
                    90g: %{{ r.return_rate }}
                  </div>
                </div>
              </div>

              <button
                class="lg:w-24 shrink-0 rounded-lg border border-n-weak px-3 py-2 text-sm font-semibold text-n-slate-11 hover:bg-n-solid-2 focus:outline-none focus:ring-2 focus:ring-woot-500"
                @click="openAgent = openAgent === r.user_id ? null : r.user_id"
              >
                {{ openAgent === r.user_id ? 'Kapat' : 'Detay' }}
              </button>
            </div>

            <!-- detay -->
            <div
              v-if="openAgent === r.user_id"
              class="border-t border-n-weak bg-n-solid-2 px-5 py-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm"
            >
              <div>
                <div class="text-n-slate-10 font-semibold">
                  Gönderdiği mesaj
                </div>
                <div class="text-lg font-bold text-n-slate-12 tabular-nums">
                  {{ nf(r.messages) }}
                </div>
              </div>
              <div>
                <button
                  class="text-n-slate-10 font-semibold text-left focus:outline-none focus:ring-2 focus:ring-woot-500 rounded"
                  @click="helpKey = 'gece'"
                >
                  🌙 Gece biriken (21:00–09:00)
                </button>
                <div class="text-lg font-bold text-n-slate-12 tabular-nums">
                  {{ fmtDuration(r.offhours_response_min) }}
                  <span
                    v-if="r.offhours_response_count"
                    class="text-sm font-semibold text-n-slate-10"
                  >
                    · {{ r.offhours_response_count }} konuşma
                  </span>
                </div>
                <div class="text-xs text-n-slate-10">
                  Performans değil, kapsama açığı
                </div>
              </div>
              <div>
                <button
                  class="text-n-slate-10 font-semibold text-left focus:outline-none focus:ring-2 focus:ring-woot-500 rounded"
                  @click="helpKey = 'iade'"
                >
                  ↩️ İade oranı (son 90 gün)
                </button>
                <div
                  class="text-lg font-bold tabular-nums"
                  :class="returnClass(r.return_rate)"
                >
                  {{
                    r.return_rate === null || r.return_rate === undefined
                      ? '—'
                      : `%${r.return_rate}`
                  }}
                </div>
                <div class="text-xs text-n-slate-10">
                  {{ nf(r.return_rate_returns) }} iade /
                  {{ nf(r.return_rate_orders) }} sipariş
                </div>
              </div>
              <div>
                <div class="text-n-slate-10 font-semibold">Ortalama sepet</div>
                <div class="text-lg font-bold text-n-slate-12 tabular-nums">
                  {{ formatCurrency(r.avg_order_value) }}
                </div>
              </div>
              <div>
                <div class="text-n-slate-10 font-semibold">İptal oranı</div>
                <div class="text-lg font-bold text-n-slate-12 tabular-nums">
                  %{{ r.cancel_rate }}
                  <span class="text-sm font-normal text-n-slate-10">
                    ({{ formatCurrency(r.revenue_cancelled) }})
                  </span>
                </div>
              </div>
              <div>
                <div class="text-n-slate-10 font-semibold">
                  Günlük ort. mesai
                </div>
                <div class="text-lg font-bold text-n-slate-12 tabular-nums">
                  {{ r.avg_daily_hours }} saat
                </div>
              </div>
              <div class="sm:col-span-2 lg:col-span-4">
                <div class="text-n-slate-10 font-semibold mb-1">
                  Kanal dağılımı
                </div>
                <div class="flex flex-wrap gap-2">
                  <span
                    v-for="(v, ch) in r.channels"
                    :key="ch"
                    class="rounded-lg bg-n-solid-1 border border-n-weak px-3 py-1.5 font-semibold text-n-slate-11"
                  >
                    {{
                      ch === 'ticimax'
                        ? 'Koza Butik'
                        : ch === 'shopify'
                          ? 'ModaXLarge'
                          : ch
                    }}: {{ v.orders }} sipariş · {{ formatCurrency(v.revenue) }}
                  </span>
                  <span
                    v-if="!Object.keys(r.channels || {}).length"
                    class="text-n-slate-10"
                    >Bu dönemde sipariş yok.</span
                  >
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ============================ BOT ============================ -->
        <template v-if="botRows.length">
          <h2 class="text-xl font-bold text-n-slate-12 mb-3">
            🤖 Otomatik Asistan
            <span class="text-sm font-normal text-n-slate-10"
              >— kıyas için ayrı tutulur, ekip toplamına girmez</span
            >
          </h2>
          <div
            v-for="r in botRows"
            :key="r.user_id"
            class="rounded-2xl border border-dashed border-n-weak bg-n-solid-2 p-5 flex flex-col sm:flex-row sm:items-center gap-4 mb-3"
          >
            <div class="flex items-center gap-3 sm:w-64 shrink-0">
              <span
                class="w-12 h-12 rounded-full bg-n-solid-3 flex items-center justify-center text-2xl shrink-0"
                >🤖</span
              >
              <span class="font-bold text-n-slate-11 text-lg truncate">{{
                r.name
              }}</span>
            </div>
            <div
              class="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-3 text-center text-n-slate-11"
            >
              <div>
                <div class="text-2xl font-bold tabular-nums">
                  {{ nf(r.conversations) }}
                </div>
                <div class="text-xs">💬 konuşma</div>
              </div>
              <div>
                <div class="text-2xl font-bold tabular-nums">
                  {{ nf(r.orders_net) }}
                </div>
                <div class="text-xs">📦 net sipariş</div>
              </div>
              <div>
                <div class="text-2xl font-bold tabular-nums">
                  {{ formatCurrency(r.revenue_net) }}
                </div>
                <div class="text-xs">💰 net ciro</div>
              </div>
              <div>
                <div class="text-2xl font-bold tabular-nums">
                  {{ r.conversion === null ? '—' : `%${r.conversion}` }}
                </div>
                <div class="text-xs">📈 dönüşüm</div>
              </div>
            </div>
          </div>
        </template>
      </template>
    </div>

    <!-- ====================== AÇIKLAMA PENCERESİ ====================== -->
    <div
      v-if="helpKey && HELP[helpKey]"
      class="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-5"
      role="dialog"
      aria-modal="true"
      @click.self="helpKey = null"
      @keydown.esc="helpKey = null"
    >
      <div
        class="max-w-xl w-full rounded-2xl bg-n-solid-1 border border-n-weak p-6 shadow-xl"
      >
        <div class="flex items-start justify-between gap-4 mb-3">
          <h3 class="text-2xl font-bold text-n-slate-12">
            {{ HELP[helpKey].title }}
          </h3>
          <button
            class="rounded-lg border border-n-weak px-3 py-1.5 text-sm font-bold text-n-slate-11 hover:bg-n-solid-3 focus:outline-none focus:ring-2 focus:ring-woot-500"
            @click="helpKey = null"
          >
            Kapat ✕
          </button>
        </div>
        <p class="text-lg leading-relaxed text-n-slate-12">
          {{ HELP[helpKey].text }}
        </p>
      </div>
    </div>
  </div>
</template>
